import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import styles from './styles/ClassroomCanvasNode.module.css';

const ClassroomCanvasNode = ({
    data,
    isConnectable,
    selected,
    showHandles = true,
    edgeConnecting,
    id,
    students,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const DISPLAY_LIMIT = 3;

    let nodeClassName = styles.nodeContainer;

    if (selected) {
        nodeClassName += ` ${styles.selected}`;
    }
    if (edgeConnecting) {
        nodeClassName += ` ${styles.edgeConnecting}`;
    }
    if (data.endpointType === 'start') {
        nodeClassName += ` ${styles.startNode}`;
    } else if (data.endpointType === 'end') {
        nodeClassName += ` ${styles.endNode}`;
    } else {
        nodeClassName += ` ${styles.regularNode}`;
    }

    const fillColor = data.fillColor || '#fff';
    let textColor = '#000';


    if (data.fillColor) {
        let hex = data.fillColor.replace('#', '');

        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('');
        }

        const num = parseInt(hex, 16);

        const r = (num >> 16) & 0xff;
        const g = (num >> 8) & 0xff;
        const b = num & 0xff;

        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        if (luminance < 128) {
            textColor = '#fff';
        }
    }

    const shouldShowToggle = students?.length > DISPLAY_LIMIT;
    const displayedStudents = isExpanded ? students : students?.slice(0, DISPLAY_LIMIT);

    const handleToggleClick = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div
            className={nodeClassName}
            style={{
                '--fill-color': fillColor,
                '--border-color': data.borderColor || '#ddd',
            }}
        >
            {students?.length > 0 && (
                <div className={styles.studentsContainer}>
                    {displayedStudents.map((s) => (
                        <span key={s.studentId} className={styles.studentTag}>
                            {s.studentName}
                        </span>
                    ))}
                    {shouldShowToggle && (
                        <button
                            className={styles.expandButton}
                            onClick={handleToggleClick}
                        >
                            {isExpanded ? 'Collapse' : `...and ${students.length - DISPLAY_LIMIT} more`}
                        </button>
                    )}
                </div>
            )}

            {(data.endpointType === 'start' || data.endpointType === 'end') && (
                <div className={styles.nodeLabel}>
                    {data.endpointType.toUpperCase()}
                </div>
            )}

            <div className={styles.nodeContent} style={{
                color: textColor,
            }}>
                {data.title || 'Untitled'}
            </div>
            
            {showHandles && (
                <>
                    <Handle
                        type="target"
                        position={Position.Left}
                        className={styles.handleTarget}
                        isConnectable={isConnectable}
                    />
                    {data.endpointType !== 'end' && (
                        <Handle
                            type="source"
                            position={Position.Right}
                            className={styles.handleSource}
                            isConnectable={isConnectable}
                        />
                    )}
                </>
            )}
        </div>
    );
};

// Memoized so a student moving between two nodes re-renders only those two
// nodes; every other node keeps its props (stable arrays from the surgical
// position updates) and skips rendering.
export default React.memo(ClassroomCanvasNode);
